import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import ResultCard from "@/components/ResultCard";

const PLACEHOLDERS = {
  text: "e.g. “she's so delulu, it's giving main character”",
  emoji: "e.g. “what does 💀🫠 mean?”",
  behavior: "e.g. “he's been quiet and staying in his room after school”",
};
const PRESETS = {
  text: ["rizz", "no cap", "mid", "looksmaxxing", "gyat"],
  emoji: ["💀", "🧢", "🫠", "😭", "🔥"],
  behavior: ["Quiet after school", "Glued to a game", "More secretive lately"],
};

export default function DecodeTool({ tool, childId, onSaved }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState("");

  const submit = async (value) => {
    const input = (value ?? text).trim();
    setError("");
    if (!input) { setError("Type something to decode"); return; }
    setLoading(true); setResult(null); setSaved(false); setFeedback(null);
    try {
      const { data } = await api.post("/decode", { tool, text: input, child_id: childId || null });
      setResult(data);
      onSaved && onSaved();
    } catch (e) {
      const code = e.response?.status;
      if (code === 402) setError("Your access has ended. Subscribe from the Account page to keep decoding.");
      else if (code === 429) setError(apiErr(e.response?.data?.detail));
      else setError(apiErr(e.response?.data?.detail) || "We couldn't decode that. Try again.");
    } finally { setLoading(false); }
  };

  const doSave = async () => {
    if (!result?.id) return;
    try { const { data } = await api.post(`/history/${result.id}/save`); setSaved(data.saved); toast.success(data.saved ? "Saved to your deck" : "Removed"); }
    catch { toast.error("Couldn't save"); }
  };
  const doFeedback = async (h) => {
    if (!result?.id) return;
    setFeedback(h);
    try { await api.post(`/history/${result.id}/feedback`, { helpful: h }); toast.success("Thanks for the feedback"); } catch {}
  };

  return (
    <div className="space-y-4">
      <div className="dk-raised rounded-2xl p-5 border" data-testid={`decode-tool-${tool}`}>
        <textarea className="dk-input resize-none" rows={2} value={text} maxLength={500} placeholder={PLACEHOLDERS[tool]} onChange={(e) => setText(e.target.value)} data-testid={`decode-input-${tool}`} />
        {error && <p className="text-sm mt-2" style={{ color: "var(--error)" }} data-testid="decode-error">{error}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          {PRESETS[tool].map((p) => (
            <button key={p} className="badge-soft hover:opacity-80" onClick={() => { setText(p); submit(p); }} data-testid="decode-preset-chip">{p}</button>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <button className="btn btn-primary" disabled={loading} onClick={() => submit()} data-testid={`decode-submit-${tool}`}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {loading ? "Decoding…" : "Decode it"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="dk-card p-6 space-y-3 anim-in">
          <div className="h-5 w-40 rounded" style={{ background: "var(--card-alt)" }} />
          <div className="h-4 w-full rounded" style={{ background: "var(--card-alt)" }} />
          <div className="h-4 w-2/3 rounded" style={{ background: "var(--card-alt)" }} />
        </div>
      )}
      {result && !loading && <ResultCard result={result} onSave={doSave} onFeedback={doFeedback} saved={saved} feedback={feedback} />}
    </div>
  );
}
